import React from 'react';
import { Button, Spin, Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import s from './s.module.less';
import Papa from 'papaparse';
import type { UploadChangeParam } from 'antd/es/upload';
import { ReviewCSVType, ReviewData } from '../../types';
import UploadIcon from 'components/UploadPatientListModal/components/SecondStep/upload.svg';
import dayjs from 'utils/dayjs';

const { Dragger } = Upload;

/**
 * 格式化日期字符串为 YYYY-MM-DD 格式
 * 支持多种日期格式：
 * - YYYYMMDD (例如: 20250502)
 * - YYYY-MM-DD (例如: 2025-05-02)
 * - YYYY/MM/DD (例如: 2025/05/02)
 * - MM/DD/YYYY (例如: 05/02/2025)
 * - DD/MM/YYYY (例如: 02/05/2025)
 */
const formatDate = (dateStr: string): string | null => {
    if (!dateStr || typeof dateStr !== 'string') {
        return null;
    }

    const trimmed = dateStr.trim();
    if (!trimmed) {
        return null;
    }

    // 尝试使用 dayjs 解析常见格式
    const formats = [
        'YYYYMMDD',
        'YYYY-MM-DD',
        'YYYY/MM/DD',
        'MM/DD/YYYY',
        'DD/MM/YYYY',
        'YYYY.MM.DD',
        'MM-DD-YYYY',
        'DD-MM-YYYY',
    ];

    for (const format of formats) {
        const parsed = dayjs(trimmed, format, true);
        if (parsed.isValid()) {
            return parsed.format('YYYY-MM-DD');
        }
    }

    // 如果所有格式都失败，尝试直接解析
    const directParse = dayjs(trimmed);
    if (directParse.isValid()) {
        return directParse.format('YYYY-MM-DD');
    }

    return null;
};

type Props = {
    onUpdate: (data?: ReviewData[]) => void;
    onError?: (error: string) => void;
    error?: string;
    remainingQuota?: number; // 剩余可上传条数
};

const SecondStep = (props: Props) => {
    const { onUpdate, onError, error, remainingQuota } = props;
    const [loading, setLoading] = React.useState(false);
    const [fileList, setFileList] = React.useState<UploadFile<any>[]>([]);
    const [fileName, setFileName] = React.useState<string>('');

    const parseCsvFile = (file?: File) => {
        if (!file) {
            return;
        }

        setLoading(true);
        Papa.parse(file, {
            complete: (result: any) => {
                try {
                    const pendingData: ReviewCSVType[] = result.data;

                    // 调试：打印解析的数据
                    // eslint-disable-next-line no-console
                    console.log('Parsed CSV data:', pendingData);
                    // eslint-disable-next-line no-console
                    console.log('First item keys:', pendingData[0] ? Object.keys(pendingData[0]) : []);

                    // 获取所有可能的字段名（处理大小写和空格变化）
                    const getFieldValue = (item: any, possibleNames: string[]): string | undefined => {
                        for (const name of possibleNames) {
                            if (item[name] !== undefined && item[name] !== null && item[name] !== '') {
                                return String(item[name]).trim();
                            }
                        }
                        // 尝试不区分大小写的匹配
                        const itemKeys = Object.keys(item);
                        for (const key of itemKeys) {
                            for (const name of possibleNames) {
                                if (key.toLowerCase().trim() === name.toLowerCase().trim()) {
                                    const value = item[key];
                                    if (value !== undefined && value !== null && value !== '') {
                                        return String(value).trim();
                                    }
                                }
                            }
                        }
                        return undefined;
                    };

                    const reviewList: ReviewData[] = pendingData
                        ?.filter((item: any) => {
                            // 支持多种字段名变体
                            const reviewContent = getFieldValue(item, ['review', 'Review', 'REVIEW', 'review content', 'Review Content']);
                            const dateValue = getFieldValue(item, ['date', 'Date', 'DATE', 'last date', 'Last Date', 'LAST DATE', 'lastdate', 'LastDate']);

                            // 检查是否有 review 内容和日期值
                            if (!reviewContent || !dateValue) {
                                return false;
                            }
                            // 检查日期是否可以格式化
                            const formattedDate = formatDate(dateValue);
                            if (!formattedDate) {
                                // eslint-disable-next-line no-console
                                console.warn('Failed to format date:', dateValue);
                            }
                            return !!formattedDate;
                        })
                        ?.map((item: any) => {
                            // 获取字段值
                            const reviewContent = getFieldValue(item, ['review', 'Review', 'REVIEW', 'review content', 'Review Content']) || '';
                            const dateValue = getFieldValue(item, ['date', 'Date', 'DATE', 'last date', 'Last Date', 'LAST DATE', 'lastdate', 'LastDate']) || '';
                            const formattedDate = formatDate(dateValue);

                            return {
                                reviewContent,
                                date: formattedDate || '',
                            };
                        })
                        ?.filter((item: ReviewData) => item.date && item.reviewContent) || [];

                    if (reviewList.length === 0) {
                        onError?.('No valid reviews found in the CSV file. Please check the file format.');
                        onUpdate?.(undefined);
                        setFileName('');
                        setFileList([]);
                    } else if (remainingQuota !== undefined && reviewList.length > remainingQuota) {
                        onError?.(`You can only upload ${remainingQuota} more review(s). Please reduce the number of reviews.`);
                        onUpdate?.(undefined);
                        setFileName('');
                        setFileList([]);
                    } else {
                        onUpdate?.(reviewList);
                        onError?.('');
                    }
                } catch (e) {
                    console.error(e);
                    onError?.('Failed to parse CSV file. Please check the file format.');
                    onUpdate?.(undefined);
                    setFileName('');
                    setFileList([]);
                } finally {
                    setLoading(false);
                }
            },
            error: (parseError: any) => {
                console.error('CSV parsing error:', parseError);
                onError?.('Failed to parse CSV file. Please check the file format.');
                onUpdate?.(undefined);
                setFileName('');
                setFileList([]);
                setLoading(false);
            },
            header: true,
        });
    };

    const handleChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
        let fList = [...info.fileList];

        // 限制文件数量
        fList = fList.slice(-1);
        // 读取文件并解析
        if (fList.length > 0) {
            const file = fList[0].originFileObj;
            if (file) {
                // 检查文件大小 (10MB)
                if (file.size > 10 * 1024 * 1024) {
                    onError?.('File size exceeds 10MB limit. Please retry.');
                    setFileList([]);
                    setFileName('');
                    onUpdate?.(undefined);
                    return;
                }
                setFileName(file.name);
                parseCsvFile(file);
            }
        }

        setFileList(fList);
    };

    const uploadProps: UploadProps = {
        name: 'file',
        multiple: false,
        onChange: handleChange,
        accept: '.csv, application/vnd.ms-excel, text/csv',
        showUploadList: false,
        fileList,
        onDrop() {
            // Handle drop
        },
        customRequest: async () => {
            // Custom request not needed for local parsing
        },
    };

    return (
        <div className={s.wrap}>
            <h3>Upload the patient reviews .csv file</h3>
            {!fileName && (
                <div className={s.uploadWrap}>
                    <Dragger {...uploadProps}>
                        <Spin spinning={loading}>
                            <p className={s.iconWrap}>
                                <img src={UploadIcon} className={s.icon} alt="upload" />
                            </p>
                            <p className={s.uploadText}>
                                <span>Upload a file</span> or drag and drop
                            </p>
                            <p className={s.uploadHint}>
                                .csv up to 10MB
                            </p>
                        </Spin>
                    </Dragger>
                </div>
            )}
            {fileName && !error && (
                <div className={s.fileUpload}>
                    <div className={s.fileInfo}>
                        <div className={s.fileIcon}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <span className={s.fileName}>{fileName}</span>
                    </div>
                    <Button
                        onClick={() => {
                            setFileList([]);
                            setFileName('');
                            onUpdate?.(undefined);
                            onError?.('');
                        }}
                        className={s.removeBtn}
                        danger
                    >
                        Remove
                    </Button>
                </div>
            )}
            {error && (
                <div className={s.errorMessage}>
                    Upload failed. {error} Please retry.
                </div>
            )}
        </div>
    );
};

export default SecondStep;
